import { supabase } from '@/lib/supabaseClient';
import {
  BuyerRecord,
  PigSaleRecord,
  PigSaleItemRecord,
  CreatePigSalePayload,
  RateRuleRecord,
  TransactionCategoryRecord,
} from '@/types/database';
import { cycleService } from '@/services/cycleService';
import { documentService } from '@/services/documentService';

export interface PigSalesFilters {
  cycleId?: string;
  buyerId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export const salesService = {
  /**
   * Fetches all active, non-void buyers ordered by name.
   */
  async getBuyers(): Promise<BuyerRecord[]> {
    const { data, error } = await supabase
      .from('buyers')
      .select('*')
      .eq('is_void', false)
      .eq('is_active', true)
      .order('buyer_name', { ascending: true });

    if (error) {
      console.warn('[salesService.getBuyers] Error:', error.message);
      return [];
    }
    return (data || []) as BuyerRecord[];
  },

  /**
   * Fetches revenue transaction categories (specifically Accounts 1010 Pig Sales & 1011 Culled Pig Sales).
   */
  async getTransactionCategories(): Promise<TransactionCategoryRecord[]> {
    const { data, error } = await supabase
      .from('transaction_categories')
      .select('*')
      .eq('is_void', false)
      .eq('category_type', 'revenue')
      .order('code', { ascending: true });

    if (error) {
      console.warn('[salesService.getTransactionCategories] Error:', error.message);
      return [];
    }
    return (data || []) as TransactionCategoryRecord[];
  },

  /**
   * Queries rate_rules for an applicable active shrinkage rate suggestion on the given sale date.
   * Prioritizes exact grower match -> exact farm match -> standard global rule.
   */
  async getRateRuleSuggestion(
    saleDate: string,
    farmId?: string | null,
    growerId?: string | null
  ): Promise<RateRuleRecord | null> {
    try {
      const { data, error } = await supabase
        .from('rate_rules')
        .select('*')
        .eq('is_void', false)
        .eq('is_active', true)
        .eq('status', 'active')
        .ilike('rule_category', '%shrink%')
        .lte('effective_start_date', saleDate);

      if (error || !data || data.length === 0) {
        return null;
      }

      // Filter by end date
      const activeRules = (data as RateRuleRecord[]).filter((r) => {
        if (!r.effective_end_date) return true;
        return r.effective_end_date >= saleDate;
      });

      if (activeRules.length === 0) return null;

      // Priority 1: Exact grower match
      if (growerId) {
        const growerMatch = activeRules.find((r) => r.grower_id === growerId);
        if (growerMatch) return growerMatch;
      }

      // Priority 2: Exact farm match
      if (farmId) {
        const farmMatch = activeRules.find((r) => r.farm_id === farmId);
        if (farmMatch) return farmMatch;
      }

      // Priority 3: Global standard rule
      const globalMatch = activeRules.find((r) => !r.grower_id && !r.farm_id);
      if (globalMatch) return globalMatch;

      return activeRules[0];
    } catch (err: any) {
      console.warn('[salesService.getRateRuleSuggestion] Error querying rate rules:', err.message);
      return null;
    }
  },

  /**
   * Fetches pig sales with optional filters.
   */
  async getPigSales(filters: PigSalesFilters = {}): Promise<PigSaleRecord[]> {
    let query = supabase
      .from('pig_sales')
      .select(`
        *,
        buyers (id, buyer_name, buyer_code, contact_person, contact_number, address),
        production_cycles (
          id,
          cycle_code,
          batch_code,
          status,
          growers (id, full_name, grower_code),
          farms (id, farm_name)
        ),
        transaction_categories (id, code, name),
        rate_rules (id, rule_name, rule_code, rate_value, rate_unit),
        pig_sale_items (*)
      `)
      .eq('is_void', false)
      .order('sale_date', { ascending: false });

    if (filters.cycleId) {
      query = query.eq('cycle_id', filters.cycleId);
    }
    if (filters.buyerId) {
      query = query.eq('buyer_id', filters.buyerId);
    }
    if (filters.startDate) {
      query = query.gte('sale_date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('sale_date', filters.endDate);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[salesService.getPigSales] Error:', error.message);
      throw error;
    }

    let results = (data || []) as unknown as PigSaleRecord[];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      results = results.filter((s) => {
        const ticket = s.scale_ticket_no?.toLowerCase() || '';
        const gatePass = s.gate_pass_no?.toLowerCase() || '';
        const saleNum = s.sale_number?.toLowerCase() || '';
        const buyer = s.buyers?.buyer_name?.toLowerCase() || '';
        const cycle = s.production_cycles?.cycle_code?.toLowerCase() || '';
        const grower = s.production_cycles?.growers?.full_name?.toLowerCase() || '';
        return (
          ticket.includes(term) ||
          gatePass.includes(term) ||
          saleNum.includes(term) ||
          buyer.includes(term) ||
          cycle.includes(term) ||
          grower.includes(term)
        );
      });
    }

    return results;
  },

  /**
   * Fetches a single sale lift by ID with full item details and attachments.
   */
  async getPigSaleById(id: string): Promise<PigSaleRecord> {
    const { data, error } = await supabase
      .from('pig_sales')
      .select(`
        *,
        buyers (id, buyer_name, buyer_code, contact_person, contact_number, email, address),
        production_cycles (
          id,
          cycle_code,
          batch_code,
          status,
          operation_type,
          growers (id, full_name, grower_code, contact_number, address),
          farms (id, farm_name, barangay, municipality_city)
        ),
        transaction_categories (id, code, name),
        rate_rules (id, rule_name, rule_code, rate_value, rate_unit),
        pig_sale_items (*)
      `)
      .eq('id', id)
      .single();

    if (error || !data) {
      throw new Error(`Sale lift not found: ${error?.message || id}`);
    }

    const record = data as unknown as PigSaleRecord;
    if (record.pig_sale_items) {
      record.pig_sale_items.sort((a, b) => a.sequence_no - b.sequence_no);
    }

    return record;
  },

  /**
   * Creates a new Pig Sale lift along with its granular scale weight items.
   * Strictly enforces physical headcount integrity at posting time.
   * Performs atomic creation with rollback cleanup if item batch fails.
   */
  async createPigSale(payload: CreatePigSalePayload): Promise<PigSaleRecord> {
    const {
      cycle_id,
      buyer_id,
      category_id,
      sale_date,
      price_per_kg,
      shrinkage_rate_applied,
      rate_rule_id,
      scale_ticket_no,
      gate_pass_no,
      vehicle_plate_no,
      notes,
      scale_items,
      attached_file,
      admin_override,
      override_reason,
    } = payload;

    if (!cycle_id) throw new Error('Production Cycle is required.');
    if (!buyer_id) throw new Error('Buyer is required.');
    if (!sale_date) throw new Error('Sale Date is required.');
    if (!scale_items || scale_items.length === 0) {
      throw new Error('At least one scale weight item is required.');
    }
    if (!price_per_kg || price_per_kg <= 0) {
      throw new Error('Price per kg must be greater than zero.');
    }

    // Step 1: Fresh At-Posting Headcount Integrity Check
    const cycleDetail = await cycleService.getCycleDetail(cycle_id);
    const availableLiveHeadcount = cycleDetail.headcount.expectedCurrentHeadcount;
    const attemptedSoldHeadCount = scale_items.reduce(
      (sum, it) => sum + Math.max(1, parseInt(String(it.head_count || 1), 10) || 1),
      0
    );

    if (attemptedSoldHeadCount > availableLiveHeadcount) {
      if (!admin_override) {
        throw new Error(
          `Over-selling blocked: Cannot sell ${attemptedSoldHeadCount} head. Cycle ${cycleDetail.cycle.cycle_code} has only ${availableLiveHeadcount} live head remaining.`
        );
      }
      if (!override_reason || override_reason.trim().length === 0) {
        throw new Error('Admin override requires a mandatory reason.');
      }
    }

    // Step 2: Accurate Derived Calculations
    const shrinkRate = Number(shrinkage_rate_applied) || 0;
    const grossWeightKg = Number(
      scale_items.reduce((sum, it) => sum + (Number(it.gross_weight_kg) || 0), 0).toFixed(2)
    );
    if (grossWeightKg <= 0) {
      throw new Error('Total Gross Weight must be greater than zero.');
    }

    const headCount = attemptedSoldHeadCount;
    const shrinkageDeductionKg = Number(
      scale_items
        .reduce((sum, it) => {
          const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
          return sum + shrinkRate * qty;
        }, 0)
        .toFixed(2)
    );

    const netWeightKg = Number(
      scale_items
        .reduce((sum, it) => {
          const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
          const gross = Number(it.gross_weight_kg) || 0;
          const shrink = shrinkRate * qty;
          return sum + Math.max(0, gross - shrink);
        }, 0)
        .toFixed(2)
    );

    if (netWeightKg <= 0) {
      throw new Error(
        `Net sale weight (${netWeightKg} kg) must be greater than zero. Check gross weight vs shrinkage rate.`
      );
    }

    const totalAmount = Number(
      scale_items
        .reduce((sum, it) => {
          const qty = Math.max(1, parseInt(String(it.head_count || 1), 10) || 1);
          const gross = Number(it.gross_weight_kg) || 0;
          const shrink = shrinkRate * qty;
          const net = Math.max(0, gross - shrink);
          const linePrice = Number(it.price_per_kg) || Number(price_per_kg) || 0;
          return sum + net * linePrice;
        }, 0)
        .toFixed(2)
    );

    const effectivePricePerKg = netWeightKg > 0 ? Number((totalAmount / netWeightKg).toFixed(2)) : (Number(price_per_kg) || 0);

    // Compile notes with audit info if overridden
    let finalNotes = notes || '';
    if (admin_override && attemptedSoldHeadCount > availableLiveHeadcount) {
      finalNotes = `[Admin Over-selling Override: ${override_reason}] ${finalNotes}`.trim();
    }

    // Unique sale number: SALE-YYYYMMDD-XXXX
    const dateCompact = sale_date.replace(/-/g, '');
    const randSuffix = Math.floor(1000 + Math.random() * 9000);
    const saleNumber = `SALE-${dateCompact}-${randSuffix}`;

    // Step 3 & 4: True Transactional Atomicity via PostgreSQL RPC
    const salePayload = {
      sale_number: saleNumber,
      cycle_id,
      buyer_id,
      category_id: category_id || null,
      sale_date,
      head_count: headCount,
      gross_weight_kg: grossWeightKg,
      shrinkage_rate_applied: shrinkRate,
      shrinkage_deduction_kg: shrinkageDeductionKg,
      net_weight_kg: netWeightKg,
      price_per_kg: effectivePricePerKg,
      total_amount: totalAmount,
      scale_ticket_no: scale_ticket_no || null,
      gate_pass_no: gate_pass_no || null,
      vehicle_plate_no: vehicle_plate_no || null,
      rate_rule_id: rate_rule_id || null,
      notes: finalNotes || null,
      admin_override: !!admin_override,
      override_reason: override_reason || null,
    };

    const itemsPayload = scale_items.map((item, idx) => {
      const itemQty = Math.max(1, parseInt(String(item.head_count || 1), 10) || 1);
      const itemGross = Number(item.gross_weight_kg) || 0;
      const itemShrink = Number((shrinkRate * itemQty).toFixed(2));
      const itemNet = Number(Math.max(0.01, itemGross - itemShrink).toFixed(2));
      const itemPrice = Number(item.price_per_kg) || effectivePricePerKg;
      const itemTotal = Number((itemNet * itemPrice).toFixed(2));

      return {
        sequence_no: item.sequence_no || idx + 1,
        head_count: itemQty,
        gross_weight_kg: itemGross,
        shrinkage_kg: itemShrink,
        net_weight_kg: itemNet,
        price_per_kg: itemPrice,
        total_amount: itemTotal,
        tag_no: item.tag_no || null,
        pen_id: item.pen_id || null,
        notes: item.notes || null,
      };
    });

    let saleId: string;

    // Call PostgreSQL atomic RPC function (commits header + items together or nothing)
    const { data: rpcData, error: rpcErr } = await supabase.rpc('create_pig_sale_atomic', {
      p_sale: salePayload,
      p_items: itemsPayload,
    });

    if (!rpcErr && rpcData?.id) {
      saleId = rpcData.id;
    } else {
      // In case RPC function is not yet deployed in mock/local environment, fallback with explicit error-checked compensating cleanup
      console.warn('[salesService.createPigSale] RPC transaction unavailable, using client-side creation:', rpcErr?.message);

      const { data: insertedSale, error: saleErr } = await supabase
        .from('pig_sales')
        .insert({
          ...salePayload,
          is_void: false,
        })
        .select()
        .single();

      if (saleErr || !insertedSale) {
        console.error('[salesService.createPigSale] Error creating sale header:', saleErr?.message);
        throw new Error(`Failed to create pig sale: ${saleErr?.message || rpcErr?.message}`);
      }

      saleId = insertedSale.id;

      const itemsToInsert = itemsPayload.map((item) => ({
        ...item,
        sale_id: saleId,
        is_void: false,
      }));

      const { error: itemsErr } = await supabase.from('pig_sale_items').insert(itemsToInsert);

      if (itemsErr) {
        console.error('[salesService.createPigSale] Error inserting sale items, executing verified compensating soft-void:', itemsErr.message);

        const { error: voidErr } = await supabase
          .from('pig_sales')
          .update({
            is_void: true,
            void_reason: `System rollback: Scale items insertion failed (${itemsErr.message})`,
            voided_at: new Date().toISOString(),
          })
          .eq('id', saleId);

        if (voidErr) {
          console.error('[salesService.createPigSale] CRITICAL: Compensating soft-void failed:', voidErr.message);
          throw new Error(
            `Failed to create scale items (${itemsErr.message}) and compensating rollback failed (${voidErr.message}). Orphaned sale ID: ${saleId}`
          );
        }

        throw new Error(`Failed to create scale items: ${itemsErr.message}. The orphaned sale header has been verified and marked void.`);
      }
    }

    // Step 5: Safe Photo / Document Attachment
    let attachmentError: string | null = null;
    if (attached_file) {
      try {
        await documentService.uploadDocument({
          file: attached_file,
          fileName: attached_file.name,
          entityType: 'pig_sales',
          entityId: saleId,
          documentType: 'weighing_sheet',
          notes: `Weighing scale ticket for sale ${saleNumber}`,
        });
      } catch (docErr: any) {
        console.warn('[salesService.createPigSale] Warning: Document attachment failed:', docErr.message);
        attachmentError = docErr.message || 'Scale ticket photo upload failed';
      }
    }

    // Step 6: Return complete created record
    const createdSale = await salesService.getPigSaleById(saleId);
    if (attachmentError && createdSale) {
      (createdSale as any).attachment_error = attachmentError;
    }
    return createdSale;
  },

  /**
   * Soft-voids a pig sale, all associated scale weight lines, and attached documents with a mandatory audit reason.
   */
  async softVoidSale(saleId: string, voidReason: string, voidedBy?: string): Promise<void> {
    if (!voidReason || voidReason.trim().length === 0) {
      throw new Error('A valid reason is mandatory for soft-voiding a sale.');
    }

    const now = new Date().toISOString();

    // 1. Soft-void pig_sales
    const { error: saleErr } = await supabase
      .from('pig_sales')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: now,
        voided_by: voidedBy || null,
      })
      .eq('id', saleId);

    if (saleErr) {
      throw new Error(`Failed to void pig sale: ${saleErr.message}`);
    }

    // 2. Soft-void all linked pig_sale_items
    const { error: itemsErr } = await supabase
      .from('pig_sale_items')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: now,
        voided_by: voidedBy || null,
      })
      .eq('sale_id', saleId);

    if (itemsErr) {
      console.warn('[salesService.softVoidSale] Error voiding sale items:', itemsErr.message);
    }

    // 3. Soft-void linked documents
    const { error: docErr } = await supabase
      .from('documents')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: now,
        voided_by: voidedBy || null,
      })
      .eq('entity_type', 'pig_sales')
      .eq('entity_id', saleId);

    if (docErr) {
      console.warn('[salesService.softVoidSale] Error voiding linked documents:', docErr.message);
    }
  },
};
